import React, { useState, useEffect } from 'react';
import { Key, Check, X } from 'lucide-react';
import { getEffectiveApiKey, setBackendApiKey } from '../../utils/backendHeaders';

interface ApiKeyModalProps {
  isOpen: boolean;
  onClose: () => void;
  endpoint: string;
}

export const ApiKeyModal: React.FC<ApiKeyModalProps> = ({ isOpen, onClose, endpoint }) => {
  const currentKey = getEffectiveApiKey(undefined, endpoint) || '';
  const [apiKeyInput, setApiKeyInput] = useState(currentKey);
  const [savedSuccess, setSavedSuccess] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setApiKeyInput(getEffectiveApiKey(undefined, endpoint) || '');
    }
  }, [isOpen, endpoint]);

  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const handleSave = (e: React.FormEvent) => {
    e.preventDefault();
    setBackendApiKey(apiKeyInput);
    setSavedSuccess(true);
    setTimeout(() => {
      setSavedSuccess(false);
      onClose();
    }, 800);
  };

  const handleClear = () => {
    setBackendApiKey('');
    setApiKeyInput('');
    setSavedSuccess(true);
    setTimeout(() => {
      setSavedSuccess(false);
      onClose();
    }, 800);
  };

  const isConfigured = Boolean(apiKeyInput.trim() || currentKey.trim());

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div
        className="modal-container"
        role="dialog"
        aria-modal="true"
        aria-labelledby="apiKeyModalTitle"
        style={{
          background: 'var(--bg-surface)',
          border: '1px solid var(--border)',
          borderRadius: '12px',
          maxWidth: '480px',
          width: '90%',
          padding: '24px',
          color: 'var(--text-secondary)',
          boxShadow: 'var(--shadow-lg)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <Key size={20} className="text-accent" style={{ color: 'var(--accent)' }} />
            <h3 id="apiKeyModalTitle" style={{ margin: 0, fontSize: '18px', color: 'var(--text-primary)' }}>Backend API Key (X-API-Key)</h3>
          </div>
          <button
            onClick={onClose}
            aria-label="Close API Key dialog"
            style={{ background: 'transparent', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', padding: '4px', minWidth: '44px', minHeight: '44px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          >
            <X size={18} />
          </button>
        </div>

        <p style={{ fontSize: '13px', color: 'var(--text-muted)', margin: '0 0 16px 0', lineHeight: '1.5' }}>
          Remote ADK agent gateways require an authenticated <code style={{ color: 'var(--accent)' }}>X-API-Key</code> request header.
        </p>

        <div style={{ background: 'var(--bg-surface-elevated)', padding: '12px', borderRadius: '8px', marginBottom: '16px', fontSize: '12px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
            <span style={{ color: 'var(--text-muted)' }}>Target Gateway:</span>
            <span style={{ color: 'var(--accent)', fontFamily: 'monospace' }}>{endpoint}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <span style={{ color: 'var(--text-muted)' }}>Status:</span>
            <span style={{ color: isConfigured ? 'var(--green)' : 'var(--red)', fontWeight: 600 }}>
              {isConfigured ? '✓ Configured' : '⚠ Missing / Unauthenticated'}
            </span>
          </div>
        </div>

        <form onSubmit={handleSave}>
          <label htmlFor="apiKeyInput" style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: 'var(--text-primary)', marginBottom: '6px' }}>
            Enter API Key:
          </label>
          <input
            id="apiKeyInput"
            type="password"
            value={apiKeyInput}
            onChange={(e) => setApiKeyInput(e.target.value)}
            placeholder="e.g. prod-fin-key-8371"
            style={{
              width: '100%',
              padding: '10px 12px',
              borderRadius: '6px',
              border: '1px solid var(--border)',
              background: 'var(--bg-surface)',
              color: 'var(--text-primary)',
              fontSize: '16px',
              fontFamily: 'monospace',
              boxSizing: 'border-box',
              marginBottom: '16px',
            }}
          />

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
            {apiKeyInput && (
              <button
                type="button"
                onClick={handleClear}
                style={{
                  padding: '8px 14px',
                  minHeight: '44px',
                  borderRadius: '6px',
                  border: '1px solid var(--red)',
                  background: 'transparent',
                  color: 'var(--red)',
                  fontSize: '13px',
                  cursor: 'pointer',
                }}
              >
                Clear
              </button>
            )}
            <button
              type="button"
              onClick={onClose}
              style={{
                padding: '8px 14px',
                minHeight: '44px',
                borderRadius: '6px',
                border: '1px solid var(--border)',
                background: 'var(--bg-surface-elevated)',
                color: 'var(--text-secondary)',
                fontSize: '13px',
                cursor: 'pointer',
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              style={{
                padding: '8px 18px',
                minHeight: '44px',
                borderRadius: '6px',
                border: 'none',
                background: savedSuccess ? 'var(--green)' : 'var(--accent)',
                color: '#fff',
                fontSize: '13px',
                fontWeight: 600,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
              }}
            >
              {savedSuccess ? (
                <>
                  <Check size={16} /> Saved!
                </>
              ) : (
                'Save Key'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
